import { parseSocialMediaPostingScheduledAt } from '@/lib/social-media/facebook-schedule-fields';

/** Hostnames that must not be used to fetch images in production (dev tunnels, LAN, etc.). */
export function isLocalOrPrivateHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.local')) {
    return true;
  }
  if (/^10\.\d+\.\d+\.\d+$/.test(host)) return true;
  if (/^192\.168\.\d+\.\d+$/.test(host)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(host)) return true;
  return false;
}

export function isLocalOrPrivateOrigin(originOrUrl: string): boolean {
  try {
    const u = new URL(originOrUrl);
    return isLocalOrPrivateHostname(u.hostname);
  } catch {
    return true;
  }
}

/** Base URL safe for server-side image download (skips dev/tunnel origins in production). */
export function normalizeImageFetchBase(url: string | null | undefined): string | null {
  if (typeof url !== 'string' || !url.trim()) return null;
  const base = url.replace(/\/$/, '');
  if (!/^https?:\/\//i.test(base)) return null;
  if (isLocalOrPrivateOrigin(base)) {
    if (process.env.NODE_ENV !== 'production') return base;
    if (process.env.ALLOW_LOCAL_IMAGE_FETCH === '1') return base;
    return null;
  }
  return base;
}

/** True when a blog/TL row already has a Facebook post linked on Graph or in social collection. */
export function contentHasLinkedFacebookPost(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
}): boolean {
  return !!(doc.facebookBlogAutoPostId?.trim() || doc.socialMediaLinkedPostId?.trim());
}

export function contentHasPendingFacebookSchedule(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingEnabled?: boolean | null;
  socialMediaPostingScheduledAt?: Date | string | null;
}): boolean {
  if (contentHasLinkedFacebookPost(doc)) return false;
  return (
    doc.socialMediaPostingEnabled === true &&
    parseSocialMediaPostingScheduledAt(doc.socialMediaPostingScheduledAt) !== null
  );
}

function contentHasFacebookComposeContext(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingEnabled?: boolean | null;
  socialMediaPostingScheduledAt?: Date | string | null;
}): boolean {
  return contentHasLinkedFacebookPost(doc) || contentHasPendingFacebookSchedule(doc);
}

/**
 * Extra images for compose UI / API — empty unless a Facebook post is already linked.
 * New compose flows must not show stale defaults from the database.
 */
export function extraImagesForCompose(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingEnabled?: boolean | null;
  socialMediaPostingScheduledAt?: Date | string | null;
  socialMediaPostingExtraImageUrls?: unknown;
}): string[] {
  if (!contentHasFacebookComposeContext(doc)) return [];
  return sanitizeSocialExtraImageUrls(doc.socialMediaPostingExtraImageUrls);
}

/** Caption for compose — empty when creating a new Facebook post (nothing linked yet). */
export function socialPostingCaptionForCompose(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingEnabled?: boolean | null;
  socialMediaPostingScheduledAt?: Date | string | null;
  socialMediaPostingCaption?: string | null;
}): string {
  if (!contentHasFacebookComposeContext(doc)) return '';
  return typeof doc.socialMediaPostingCaption === 'string' ? doc.socialMediaPostingCaption : '';
}

/** Tags for compose — empty when creating a new Facebook post. */
export function socialPostingTagsForCompose(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingEnabled?: boolean | null;
  socialMediaPostingScheduledAt?: Date | string | null;
  socialMediaPostingTags?: string[] | null;
}): string[] {
  if (!contentHasFacebookComposeContext(doc)) return [];
  return Array.isArray(doc.socialMediaPostingTags)
    ? doc.socialMediaPostingTags.map(String).map((t) => t.trim()).filter(Boolean)
    : [];
}

export function socialPostingEnabledForCompose(doc: {
  facebookBlogAutoPostId?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingScheduledAt?: Date | string | null;
  socialMediaPostingEnabled?: boolean | null;
}): boolean {
  return contentHasFacebookComposeContext(doc) && doc.socialMediaPostingEnabled === true;
}

/** Stored extra-image URLs — drop empty, localhost, and duplicates. */
export function sanitizeSocialExtraImageUrls(urls: unknown): string[] {
  if (!Array.isArray(urls)) return [];
  const out: string[] = [];
  for (const raw of urls) {
    const u = String(raw).trim();
    if (!u) continue;
    if (/^https?:\/\//i.test(u) && isLocalOrPrivateOrigin(u)) continue;
    if (!out.includes(u)) out.push(u);
  }
  return out;
}

function configuredPublicBases(): string[] {
  const candidates = [
    process.env.NEXT_PUBLIC_SITE_URL,
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.SITE_URL,
    process.env.APP_URL,
    process.env.NEXTAUTH_URL,
  ];
  return [
    ...new Set(
      candidates
        .map((c) => normalizeImageFetchBase(c))
        .filter((b): b is string => Boolean(b))
    ),
  ];
}

/** Absolute URLs to try when downloading a stored image path or URL. */
export function buildImageFetchCandidates(href: string, requestOrigin?: string | null): string[] {
  const t = href.trim();
  if (!t) return [];

  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      if (isLocalOrPrivateOrigin(u.origin)) {
        const path = `${u.pathname}${u.search}`;
        const rewritten = buildImageFetchCandidates(path, null);
        return [...new Set([...rewritten, t])];
      }
    } catch {
      /* use as-is */
    }
    return [t];
  }

  const pathPart = t.startsWith('/') ? t : `/${t}`;
  const pathParts = [pathPart];
  if (/^\/uploads\//i.test(pathPart)) {
    pathParts.push(`/api${pathPart}`);
  }
  const bases = [
    ...configuredPublicBases(),
    normalizeImageFetchBase(requestOrigin),
    normalizeImageFetchBase(process.env.INTERNAL_APP_BASE_URL),
    process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:3000' : '',
    process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '',
  ].filter(Boolean) as string[];

  const uniq = [...new Set(bases)];
  return uniq.flatMap((base) => pathParts.map((path) => `${base}${path}`));
}
